import React from 'react';
import PropTypes from 'prop-types';
import Component from 'BaseComponent';
import { withRouter, Switch, Route, Link, Redirect } from 'react-router-dom';
import autobind from 'autobind-decorator';
import Modal from 'components/Modal';
import EditAccount from 'components/EditAccount';
import Enable2FA from 'components/Enable2FA';
import Disable2FA from 'components/Disable2FA';
import EditBillingInfo from 'components/EditBillingInfo';
import Password from 'components/Password';
import EditCardInfo from 'components/EditCardInfo';
import ChangePlan from 'components/ChangePlan';
import Spinner from 'components/Spinner';
import api from 'api';
import { query } from 'redux-bees';
import Time from 'components/Time';
import Invoices from 'components/Invoices';
import differenceInDays from 'date-fns/differenceInDays';
import queryString from 'query-string';
import formatBytes from 'bytes';

@withRouter

@query('account', api.getAccount)
@query('billingProfile', api.getBillingProfile)
@query('plans', api.getPlans)

export default class Account extends Component {
  static propTypes = {
    account: PropTypes.object.isRequired,
    billingProfile: PropTypes.object,
    history: PropTypes.object.isRequired,
    status: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    plans: PropTypes.array,
  }

  @autobind
  handleCloseModal() {
    const parsedQuery = queryString.parse(this.props.location.search);
    this.props.history.push(parsedQuery.returnTo || '/account');
  }

  @autobind
  renderBillingProfile() {
    const { billingProfile: profile } = this.props;
    const { attributes } = profile;

    return (
      <div key={profile.id} className="Account__profile">
        <div className="Account__profile__content">
          <div className="Account__profile__data">
            <table className="Account__area__table">
              <tbody>
                {
                  attributes.company &&
                    <tr>
                      <th>Company</th>
                      <td>{attributes.company}</td>
                    </tr>
                }
                <tr>
                  <th>Name</th>
                  <td>
                    { attributes.firstName ?
                      <span>{attributes.firstName} {attributes.lastName}</span> :
                      <span>No billing info present!</span>
                    }
                  </td>
                </tr>
                {
                  attributes.addressLine &&
                    <tr>
                      <th>Address</th>
                      <td>
                        {attributes.addressLine},&nbsp;
                        {attributes.city},&nbsp;
                        {attributes.state},&nbsp;
                        {attributes.zip},&nbsp;
                        {attributes.country}
                      </td>
                    </tr>
                }
                {
                  attributes.cardMaskedNumber &&
                    <tr>
                      <th>Credit card</th>
                      <td>
                        {this.t(`creditCard.${attributes.cardType}`)} ({attributes.cardMaskedNumber})
                      </td>
                    </tr>
                }
                {
                  (attributes.refundableCredits + attributes.promotionalCredits) > 0 &&
                    <tr>
                      <th>Credits</th>
                      <td>
                        <div className="Account__profile__credit">
                          €{
                            (
                              attributes.refundableCredits +
                                attributes.promotionalCredits
                            ) / 100
                          }
                        </div>
                      </td>
                    </tr>
                }
              </tbody>
            </table>
          </div>

          <div className="Account__profile__actions">
            <Link to={'/account/billing_profiles/edit'} className="button button--tiny">Edit billing info</Link>
            {
              attributes.cardMaskedNumber &&
                <Link to={'/account/billing_profiles/edit_card'} className="button button--tiny">Change credit card</Link>
            }
            {
              !attributes.cardMaskedNumber &&
                <Link to={'/account/billing_profiles/edit_card'} className="button button--tiny">Add credit card</Link>
            }
          </div>
        </div>
      </div>
    );
  }

  @autobind
  renderPlan(currentPlan) {
    const { billingProfile } = this.props;
    const { attributes } = currentPlan;

    return (
      <div className="Account__profile">
        <div className="Account__profile__content">
          <div className="Account__profile__data">
            <table className="Account__area__table">
              <tbody>
                <tr>
                  <th>Current plan</th>
                  <td><strong>{attributes.name}</strong></td>
                </tr>
                {
                  billingProfile.attributes.trialEndsAt &&
                    <tr>
                      <th>Trial days left</th>
                      <td>
                        {
                          differenceInDays(
                            new Date(billingProfile.attributes.trialEndsAt),
                            new Date(),
                          )
                        }
                        &nbsp;days
                      </td>
                    </tr>
                }
                {
                  billingProfile.attributes.cancelledAt &&
                    <tr>
                      <th>Status</th>
                      <td>
                        Pending cancellation
                      </td>
                    </tr>
                }
                {
                  billingProfile.attributes.nextBillingAt &&
                    <tr>
                      <th>Next billing</th>
                      <td>
                        <Time
                          showRelative={false}
                          value={billingProfile.attributes.nextBillingAt}
                        />
                      </td>
                    </tr>
                }
                {
                  (billingProfile.attributes.nextBillingAmount
                    || billingProfile.attributes.nextBillingAmount === 0) &&
                    <tr>
                      <th>Next billing amount</th>
                      <td>
                        € {billingProfile.attributes.nextBillingAmount / 100}
                      </td>
                    </tr>
                }
                {
                  billingProfile.attributes.couponId &&
                    <tr>
                      <th>Coupon</th>
                      <td>
                        {billingProfile.attributes.couponId}
                      </td>
                    </tr>
                }
                {
                  billingProfile.attributes.couponApplyTill &&
                    <tr>
                      <th>Coupon expiration</th>
                      <td>
                        <Time
                          showRelative={false}
                          value={billingProfile.attributes.couponApplyTill}
                        />
                      </td>
                    </tr>
                }
              </tbody>
            </table>
          </div>

          <div className="Account__profile__actions">
            <Link
              to={'/account/billing_profiles/change_plan'}
              className="button button--tiny"
            >
              Change plan/recurrence
            </Link>
          </div>
        </div>
      </div>
    );
  }

  @autobind
  renderTrial() {
    const { account, billingProfile, plans } = this.props;

    const currentPlan = plans &&
      plans.find(plan => plan.id === account.relationships.plan.data.id);

    return (
      <div className="Account__area">
        <div className="Account__area__header">
          <div className="Account__area__header__title">
            Your plan
          </div>
        </div>
        <div className="Account__area__content">
          <div className="Account__profile">
            <div className="trial">
              <div className="trial__title">
                You&apos;re currently on plan <strong>{currentPlan.attributes.name}</strong>
              </div>
              <div className="trial__ending">
                Your trial will expire in&nbsp;
                {
                  differenceInDays(
                    new Date(billingProfile.attributes.trialEndsAt),
                    new Date(),
                  )
                }
                &nbsp;days
              </div>
              <div className="trial__description">
                Your account will be deactivated after the trial period. To continue
                using Hardypress, please insert your billing info, add a valid credit card
                and choose your plan.
              </div>
              <div className="trial__buttons">
                {
                  !billingProfile.attributes.addressLine &&
                    <Link to={'/account/billing_profiles/edit'} className="button">Insert billing info</Link>
                }
                {
                  !billingProfile.attributes.cardMaskedNumber &&
                    <Link to={'/account/billing_profiles/edit_card'} className="button">Add credit card</Link>
                }
                <div className="Account__trial-link">
                  <a href="https://www.hardypress.com/faq/redeem-a-coupon/" target="_blank" rel="noopener noreferrer">Do you have a coupon?</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  @autobind
  renderCancelled() {
    const { billingProfile } = this.props;

    return (
      <div className="Account__area">
        <div className="Account__area__header">
          <div className="Account__area__header__title">
            Your plan
          </div>
        </div>
        <div className="Account__area__content">
          <div className="Account__profile">
            <div className="trial">
              <div className="trial__title">
                Your subscription is <strong>expired</strong>
              </div>
              <div className="trial__description">
                Your sites have been deactivated. To reactivate them
                please insert your billing info, add a valid credit card
                and choose your plan.
              </div>
              <div className="trial__buttons">
                {
                  !billingProfile.attributes.addressLine &&
                    <Link to={'/account/billing_profiles/edit'} className="button">Insert billing info</Link>
                }
                {
                  !billingProfile.attributes.cardMaskedNumber &&
                    <Link to={'/account/billing_profiles/edit_card'} className="button">Add credit card</Link>
                }
                {
                  billingProfile.attributes.addressLine &&
                  billingProfile.attributes.cardMaskedNumber &&
                    <Link
                      to={'/account/billing_profiles/change_plan'}
                      className="button button--tiny"
                    >
                      Choose plan/recurrence
                    </Link>
                }
                <div className="Account__trial-link">
                  <a href="https://www.hardypress.com/faq/redeem-a-coupon/" target="_blank" rel="noopener noreferrer">Do you have a coupon?</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { account, billingProfile, status, plans } = this.props;

    const currentPlan = plans &&
      plans.find(plan => plan.id === account.relationships.plan.data.id);

    const renderCancelled = billingProfile && billingProfile.attributes.status === 'cancelled';

    const renderCta = billingProfile && !renderCancelled &&
                        billingProfile.attributes.trialEndsAt &&
                        (
                          !billingProfile.attributes.addressLine ||
                            !billingProfile.attributes.cardMaskedNumber
                        );

    return (
      <div>
        <div className="Backend__title space--bottom-2">
          Account
        </div>
        <div>
          <div className="Account__area">
            <div className="Account__area__header">
              <div className="Account__area__header__title">
                Your Account
              </div>
            </div>
            <div className="Account__area__content">
              <div className="Account__profile">
                <div className="Account__profile__content">
                  <div className="Account__profile__data">
                    <table className="Account__area__table">
                      <tbody>
                        <tr>
                          <th>Name</th>
                          <td>{account.attributes.name}</td>
                        </tr>
                        <tr>
                          <th>Email</th>
                          <td>{account.attributes.email}</td>
                        </tr>
                        <tr>
                          <th>Password</th>
                          <td>*********</td>
                        </tr>
                        {
                          account.attributes.apiToken &&
                          <tr>
                            <th>
                              API token
                            </th>
                            <td>
                              <Password password={account.attributes.apiToken} />
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                  <div className="Account__profile__actions">
                    <Link to="/account/edit" className="button--tiny">Edit account</Link>
                    { !account.attributes.last2faActivationAt &&
                    <Link to="/account/enable2fa" className="button--tiny">Enable 2FA</Link>
                    }
                    { account.attributes.last2faActivationAt &&
                    <Link to="/account/disable2fa" className="button--tiny">Disable 2FA</Link>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
          {
            billingProfile && renderCancelled &&
              this.renderCancelled()
          }
          {
            billingProfile && plans && renderCta &&
              this.renderTrial()
          }
          {
            billingProfile && !renderCta && !renderCancelled &&
              <div className="Account__area">
                <div className="Account__area__header">
                  <div className="Account__area__header__title">
                    Your plan
                  </div>
                </div>
                <div className="Account__area__content">
                  {
                    status.account.isLoading &&
                      <div className="spinner-container">
                        <Spinner size={40} />
                      </div>
                  }
                  {
                    currentPlan && this.renderPlan(currentPlan)
                  }
                </div>
              </div>
          }
          <div className="Account__area">
            <div className="Account__area__header">
              <div className="Account__area__header__title">
                Current usage
              </div>
            </div>
            <div className="Account__area__content">
              <div className="Account__profile">
                <div className="Account__profile__content">
                  <div className="Account__profile__data">
                    <table className="Account__area__table">
                      <tbody>
                        <tr>
                          <th>Sites</th>
                          <td>
                            {account.attributes.sitesCounter}
                            {' / '}
                            {currentPlan && (currentPlan.attributes.sitesLimit || 'Unlimited')}
                          </td>
                        </tr>
                        <tr>
                          <th>Storage</th>
                          <td>
                            {formatBytes(account.attributes.sitesSize, { unitSeparator: ' ' })}
                            {' / '}
                            {account.attributes.storageBytesLimit ? formatBytes(account.attributes.storageBytesLimit, { unitSeparator: ' ' }) : 'Unlimited'}
                          </td>
                        </tr>
                        <tr>
                          <th>Last month form submissions</th>
                          <td>
                            {account.attributes.sitesLastMonthFormSubmissionsCounter}
                            {' / '}
                            {currentPlan && (currentPlan.attributes.formSubmissionsLimit || 'Unlimited')}
                          </td>
                        </tr>
                        <tr>
                          <th>Monthly bandwidth used</th>
                          <td>
                            {formatBytes(account.attributes.monthlyBandwidthUsed, { unitSeparator: ' ' })}
                            {' / '}
                            {account.attributes.monthlyBandwidthLimit ? formatBytes(account.attributes.monthlyBandwidthLimit, { unitSeparator: ' ' }) : 'Unlimited'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="Account__profile__actions" />
                </div>
              </div>
            </div>
          </div>
          {
            billingProfile &&
              (
                billingProfile.attributes.addressLine ||
                billingProfile.attributes.cardMaskedNumber
              ) &&
              <div className="Account__area">
                <div className="Account__area__header">
                  <div className="Account__area__header__title">
                    Billing profile
                  </div>
                </div>
                <div className="Account__area__content">
                  {this.renderBillingProfile()}
                </div>
              </div>
          }
          {
            billingProfile && !renderCta &&
              <div className="Account__area">
                <div className="Account__area__header">
                  <div className="Account__area__header__title">
                    Invoices
                  </div>
                </div>
                <div className="Account__area__content">
                  <div className="Account__profile">
                    <div className="Account__profile__content">
                      <div className="Account__profile__data">
                        <Invoices />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          }
        </div>

        <Switch>
          <Route
            exact
            path="/account/edit"
            render={() => (
              <Modal wide title="Edit your account" onClose={this.handleCloseModal}>
                <EditAccount />
              </Modal>
            )}
          />
          <Route
            exact
            path="/account/enable2fa"
            render={() => (
              <Modal wide title="Enable 2FA for your account" onClose={this.handleCloseModal}>
                <Enable2FA accountEmail={account.attributes.email} />
              </Modal>
            )}
          />
          <Route
            exact
            path="/account/disable2fa"
            render={() => (
              <Modal wide title="Disable 2FA for your account" onClose={this.handleCloseModal}>
                <Disable2FA />
              </Modal>
            )}
          />
          <Route
            exact
            path="/account/billing_profiles/edit"
            render={() => (
              <Modal wide title="Edit billing profile" onClose={this.handleCloseModal}>
                <EditBillingInfo />
              </Modal>
            )}
          />
          <Route
            exact
            path="/account/billing_profiles/edit_card"
            render={() => (
              <Modal wide title="Edit credit card" onClose={this.handleCloseModal}>
                <EditCardInfo />
              </Modal>
            )}
          />
          <Route
            exact
            path="/account/billing_profiles/change_plan"
            render={() => (
              <Modal big title="Change plan" onClose={this.handleCloseModal}>
                <ChangePlan currentPlan={currentPlan} />
              </Modal>
            )}
          />
          <Redirect to="/account" />
        </Switch>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/components/Account.jsx